import React, { useMemo, Component } from 'react';
import { connect, useDispatch } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import classNames from 'classnames';
import { get, has, orderBy } from 'lodash';
import dragPreviewSrc from 'images/drag_preview.png';
import elementsActions from 'actions/editor/elements';
import {
  toggleCommentary,
  autoFetchCommentary,
} from 'actions/editor/commentary';
import { getMetaTokens, hasGlobalFeature } from 'selectors';
import { getChildElements as childrenSelector } from 'selectors/editor';
import Modal from 'components/shared/modal';
import { Icon } from 'ui';
import InfoTooltip from 'components/shared/InfoTooltip';
import { useIsFeatureEnabledForLinkedElement } from 'components/ContentLibrary/features';
import {
  Edit2,
  ExternalLink,
  Maximize2,
  Menu,
  MessageCircle,
  Minimize2,
  RefreshCw,
  Trash2,
} from 'react-feather';
import ElementLoading from 'components/shared/ElementLoading/ElementLoading';
import SaveContentModal from './library/SaveContentModal';
import ElementControls from './controls';
import DragSource from './dragSource';
import SuccessNotification from './library/SuccessNotification';
import UniversalPickerModal from './content/UniversalPickerModal';
import UnlinkSvg from './icons/UnlinkSvg';
import AddLibrarySvg from './icons/AddLibrarySvg';

/**
 * useActions returns stable redux dispatch bound action creators for use within function components
 * If your child element component is a function component you should place your callbacks here
 *
 * Please keep the dependency array for useMemo as small as possible prevent function refs from changing
 * unnecessarily.
 */
export function useActions(id) {
  const dispatch = useDispatch();
  return useMemo(
    () =>
      bindActionCreators(
        {
          handleImageChange: (file, imageType = 'content') =>
            elementsActions.handleImageChange(id, file, imageType),
          handleEventRemoval: () => elementsActions.handleEventRemoval,
          handleUpdate: changes => elementsActions.update(id, changes),
          applyChanges: (changes, options) =>
            elementsActions.applyChanges(id, changes, options),
          toggleAddToImageLibrary: image =>
            elementsActions.toggleAddToImageLibrary(image),
          requestUpdateAndApplyChanges: changes =>
            elementsActions.requestUpdateAndApplyChanges(id, changes),
        },
        dispatch,
      ),
    [id, dispatch],
  );
}

const ElementBase = ElementBody => {
  class ElementWrapper extends Component {
    constructor(props) {
      super(props);
      this.state = {
        wide: props.content?.wide,
        selectedEvent: props.content?.in_person_training_event_id,
        isLoading: false,
        noEventPermission: !props.currentUser.canViewEvents,
      };
      this.titles = {
        flipcard: t('lessons.editor.elements.flipcard'),
        gong_call: t('lessons.editor.elements.gong_call'),
        reveal: t('lessons.editor.elements.reveal'),
        media_recording: 'Media Recording',
      };
      this.closeDeleteConfirmation = this.closeDeleteConfirmation.bind(this);
      this.closeCopyModal = this.closeCopyModal.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.toggleWideMode = this.toggleWideMode.bind(this);
      this.closeUniversalPickerModal =
        this.closeUniversalPickerModal.bind(this);
      this.showSavedContentNotification =
        this.showSavedContentNotification.bind(this);
      this.hideSavedContentNotification =
        this.hideSavedContentNotification.bind(this);
      this.removeSelectedEvent = this.removeSelectedEvent.bind(this);
      this.setIsLoading = this.setIsLoading.bind(this);
      this.setNoEventPermissions = this.setNoEventPermissions.bind(this);
    }

    static defaultProps = {
      reordable: true,
    };

    static elementLibraryExcludeTypes = new Set(['lesson_content_image']);

    componentDidMount() {
      this.setDragPreview();
      document.addEventListener('click', this.handleClick);
    }

    componentWillUnmount() {
      document.removeEventListener('click', this.handleClick);
    }

    get type() {
      const { type, content } = this.props;
      return type === 'question' ? content?.question_type : type;
    }

    get title() {
      return this.titles[this.type];
    }

    get isValid() {
      if (this.type === 'sorting' || this.type === 'matching') {
        return true;
      }
      return get(this.props, 'content.valid', true);
    }

    get controlsConfig() {
      const {
        props: {
          type,
          content,
          features,
          saved_element,
          isLinked,
          currentUser,
          isEnabledLinkedElement,
          ...props
        },
        state,
      } = this;
      // Use 'test' to conditionally render a control option. if omitted, the
      // control option will be always rendered.
      return {
        reorder: {
          test: type !== 'scorm' && props.reordable,
          order: 1,
          draggable: true,
          tooltip: t('actions.tooltips.move'),
          icon: <Menu />,
          connectDragSource: props.connectDragSource,
        },
        viewDetails: {
          test: type === 'seismic_content' && content?.universal_link,
          order: 2,
          icon: <ExternalLink />,
          tooltip: t('actions.tooltips.view_content_details'),
          onClick: () => {
            window.open(content?.universal_link, '_blank');
          },
        },
        clear: {
          test: type === 'lesson_content_image' && !!content?.image,
          order: 2,
          icon: <RefreshCw />,
          tooltip: t('actions.tooltips.replace_image'),
          disabled: content?.uploadInProgress,
          onClick: () => props.handleImageChange(null),
        },
        replaceEvent: {
          test: type === 'lesson_event_element',
          order: 2,
          icon: <RefreshCw />,
          tooltip: t('lessons.editor.event.replace_event'),
          disabled:
            !content?.in_person_training_event_id ||
            this.state.noEventPermission,
          onClick: this.removeSelectedEvent,
        },
        delete: {
          test: type !== 'scorm',
          order: 3,
          icon: <Trash2 />,
          tooltip: t('actions.tooltips.delete'),
          disabled:
            type === 'lesson_event_element' && this.state.noEventPermission,
          onClick: this.showDeleteConfimation.bind(this),
        },
        wide: {
          test:
            type !== 'seismic_content' &&
            type !== 'scorm' &&
            has(content, 'wide'),
          order: 4,
          icon: state.wide ? <Minimize2 /> : <Maximize2 />,
          onClick: this.toggleWideMode,
          tooltip: state.wide
            ? t('actions.tooltips.disable_wideview')
            : t('actions.tooltips.enable_wideview'),
        },
        comment: {
          order: 5,
          icon: <MessageCircle />,
          tooltip: this.commentTooltip,
          onClick: props.toggleCommentary,
          count: props.comments_count,
        },
        copy: {
          test: (() => {
            if (
              type === 'seismic_content' ||
              type === 'scorm' ||
              isLinked ||
              content?.question_type === 'conversation' ||
              type === 'role_play' ||
              type === 'lesson_event_element'
            ) {
              return false;
            }

            return !ElementWrapper.elementLibraryExcludeTypes.has(type);
          })(),
          order: 6,
          icon: <AddLibrarySvg />,
          tooltip: t('actions.tooltips.add_element_library'),
          onClick: this.showCopyModal.bind(this),
        },
        edit: {
          test:
            type !== 'seismic_content' &&
            type !== 'scorm' &&
            isLinked &&
            currentUser.canManageContentLibrary &&
            isEnabledLinkedElement,
          order: 7,
          icon: <Edit2 />,
          tooltip: t('actions.tooltips.edit_original'),
          onClick: () => {
            window.location.assign(
              // eslint-disable-next-line camelcase
              `/content/library/elements/${saved_element.id}/edit`,
            );
          },
        },
        unlink: {
          test:
            type !== 'seismic_content' &&
            type !== 'scorm' &&
            isLinked &&
            currentUser.canManageContentLibrary &&
            isEnabledLinkedElement,
          order: 7,
          icon: <UnlinkSvg />,
          tooltip: t('actions.tooltips.unlink'),
          onClick: props.unlinkElement,
        },
        replaceContent: {
          test: type === 'seismic_content' && content?.library_content_id,
          order: 2,
          icon: <RefreshCw />,
          tooltip: t('actions.tooltips.replace'),
          onClick: this.showUniversalPickerModal.bind(this),
        },
      };
    }

    get controls() {
      const controls = this.controlsConfig;
      Object.keys(controls).forEach(name => {
        // testing if we need to render the control option
        if (has(controls[name], 'test')) {
          if (!controls[name].test) {
            delete controls[name];
            return;
          }
          delete controls[name].test;
        }
        // proxy onClick event
        if (typeof controls[name].onClick === 'function') {
          const callback = controls[name].onClick;
          controls[name].onClick = e => {
            if (e) {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }
            callback();
          };
        }
      });
      return orderBy(controls, 'order');
    }

    get deleteModalConfig() {
      return {
        type: 'confirm',
        show: this.state.showConfirmModal,
        title: 'Delete Element',
        message: 'Are you sure you want to delete this element?',
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        onConfirm: this.props.handleDelete,
        onCancel: this.closeDeleteConfirmation,
        dismissCallback: this.closeDeleteConfirmation,
      };
    }

    get copyModalConfig() {
      return {
        type: this.props.type,
        show: this.state.showCopyModal,
        dismissCallback: this.closeCopyModal,
        onSave: this.props.handleCopy,
        replaceElement: this.props.replaceElement,
        savePath: this.props.save_path,
        onCancel: this.closeCopyModal,
        showSavedContentNotification: this.showSavedContentNotification,
      };
    }

    get universalPickerModalConfig() {
      return {
        closeModal: this.closeUniversalPickerModal,
        requestUpdateAndApplyChanges: this.props.requestUpdateAndApplyChanges,
        setIsLoading: this.setIsLoading,
      };
    }

    get commentTooltip() {
      const { comments_count: count, active } = this.props;
      if (active) return t('actions.tooltips.hide_comments');
      if (count) return t('actions.tooltips.show_comments');
      return t('actions.tooltips.add_comment');
    }

    // eslint-disable-next-line consistent-return
    handleClick({ target }) {
      const {
        props: { isCommenting, active },
        contentContainer: container,
      } = this;

      // reject if clicked outside an element
      const isElement = container.contains(target) || target === container;
      if (!isElement) return false;

      const activeChild = target
        ?.closest('.EditorElement-container')
        ?.classList?.contains('EditorElement-container--active');

      const isActive = activeChild || active;
      if (isCommenting && !isActive) {
        this.props.fetchCommentary();
      }
    }

    setDragPreview() {
      const { connectDragPreview } = this.props;
      const dragPreview = new Image();
      dragPreview.src = dragPreviewSrc;
      dragPreview.onload = () =>
        connectDragPreview(dragPreview, { anchorX: 1, anchorY: 0 });
    }

    toggleWideMode() {
      this.setState(
        state => ({ wide: !state.wide }),
        () => {
          this.props.handleUpdate({ wide: this.state.wide });
          this.props.applyChanges({ wide: this.state.wide });
        },
      );
    }

    closeDeleteConfirmation() {
      this.setState({ showConfirmModal: false });
    }

    showDeleteConfimation() {
      this.setState({ showConfirmModal: true });
    }

    showCopyModal() {
      this.setState({ showCopyModal: true });
    }

    closeCopyModal() {
      this.setState({ showCopyModal: false });
    }

    showSavedContentNotification(data) {
      this.setState({ savedContentNotification: data });
    }

    hideSavedContentNotification() {
      this.setState({ savedContentNotification: null });
    }

    showUniversalPickerModal() {
      this.setState({ showUniversalPickerModal: true });
    }

    closeUniversalPickerModal() {
      this.setState({ showUniversalPickerModal: false });
    }

    removeSelectedEvent() {
      this.props.handleUpdate({ in_person_training_event_id: null });
      this.props.applyChanges({ in_person_training_event_id: null });
      this.setState({
        selectedEvent: null,
      });
    }

    setIsLoading(isLoading) {
      this.setState({ isLoading });
    }

    setNoEventPermissions() {
      this.setState({
        noEventPermission: true,
      });
    }

    render() {
      const {
        id,
        position,
        type,
        saved_element,
        handleDelete,
        handleUpdate,
        isDragging,
        isLinked,
        templateActive,
      } = this.props;

      const actions = { handleUpdate, handleDelete };

      const styleName = classNames('EditorElement', {
        'EditorElement--invalid ': !this.isValid,
        'EditorElement--disabled': templateActive,
        [`EditorElement--${type}`]: true,
      });

      const containerStyleName = classNames('EditorElement-container', {
        'EditorElement-container--wide': this.state.wide,
        'EditorElement-container--active': this.props.active,
        dragging: this.props.isDragging,
        disabled: templateActive,
      });

      const containerStyle = {
        opacity: isDragging ? 0.5 : 1,
      };

      return (
        <div
          inert={templateActive ? '' : null}
          id={`element-${id}`}
          className={styleName}
          style={containerStyle}
          /** @todo can we avoid using position as a key? (see c81d144d) */
          key={`${id}:${position}`}
        >
          <Modal {...this.deleteModalConfig} />
          <SaveContentModal {...this.copyModalConfig} />
          {this.state.showUniversalPickerModal && (
            <UniversalPickerModal {...this.universalPickerModalConfig} />
          )}
          {this.state.savedContentNotification && (
            <SuccessNotification
              content={this.state.savedContentNotification}
              dismissCallback={this.hideSavedContentNotification}
            />
          )}
          {isLinked && (
            <div className="EditorElement-linked">
              <div className="fz-18">
                {/* eslint-disable-next-line camelcase */}
                <span className="text-wrap">{saved_element.title}</span>
                <Icon name="link" className="ml-8" />
              </div>
              <InfoTooltip
                tooltip={t('lessons.editor.linked_element.tooltip')}
              />
            </div>
          )}
          <div className={containerStyleName}>
            {!this.props.templateActive && (
              <ElementControls controls={this.controls} />
            )}
            <div
              ref={node => {
                this.contentContainer = node;
              }}
            >
              <ElementLoading
                isLoading={this.state.isLoading}
                text={t('questions.types.content.loading')}
              >
                <ElementBody
                  {...this.props}
                  {...actions}
                  title={this.title}
                  selectedEvent={this.state.selectedEvent}
                  noEventPermission={this.state.noEventPermission}
                  setNoEventPermissions={this.setNoEventPermissions}
                />
              </ElementLoading>
            </div>
          </div>
        </div>
      );
    }
  }

  const mapStateToProps = () => {
    const getChildren = childrenSelector();
    return (state, { id, saved_element: savedElement }) => {
      const hasFeature = feature => hasGlobalFeature(state, feature);

      return {
        isLinked: !!savedElement,
        isCommenting: state.editor.commentary.visible,
        templateActive: state.editor.templateActive,
        active: state.editor.commentary.elementId === id,
        tokens: getMetaTokens(state),
        success: state.editor.success,
        children: getChildren(state.editor, id),
        child_count: getChildren(state.editor, id).length,
        features: {
          uploadVideo: hasFeature('upload_video'),
          ...state.meta.features,
        },
        currentUser: state.meta.currentUser,
      };
    };
  };

  const mapDispatchToProps = (dispatch, { id, position: ownPosition }) => ({
    handleDelete() {
      dispatch(elementsActions.delete(id));
    },
    uploadDocument(documentId) {
      dispatch(elementsActions.uploadDocument(id, documentId));
    },
    replaceElement(element) {
      dispatch(elementsActions.replaceElement(id, element));
    },
    order(newPosition) {
      dispatch(elementsActions.orderElement(id, ownPosition, newPosition));
    },
    unlinkElement() {
      dispatch(elementsActions.unlinkElement(id));
    },
    createChildElement(type, position) {
      dispatch(elementsActions.create(type, position, id));
    },
    detachParent(newPosition) {
      dispatch(elementsActions.detachParent(id, newPosition));
    },
    assignParent(parentId) {
      dispatch(elementsActions.assignParent(id, parentId));
    },
    toggleCommentary() {
      dispatch(toggleCommentary(id));
    },
    fetchCommentary() {
      dispatch(autoFetchCommentary(id));
    },
    toggleDragging() {
      dispatch(elementsActions.toggleDragging());
    },
    handleCopy(params, savePath) {
      return dispatch(elementsActions.copy(params, savePath));
    },
    toggleAddToImageLibrary(image) {
      dispatch(elementsActions.toggleAddToImageLibrary(image));
    },
  });

  const FunctionElementWrapper = props => {
    const isEnabledLinkedElement = useIsFeatureEnabledForLinkedElement(
      props.type,
      props.content?.question_type,
    );

    const {
      applyChanges,
      handleImageChange,
      handleUpdate,
      toggleAddToImageLibrary,
      requestUpdateAndApplyChanges,
    } = useActions(props.id);

    return (
      <ElementWrapper
        {...props}
        handleImageChange={handleImageChange}
        handleUpdate={handleUpdate}
        applyChanges={applyChanges}
        toggleAddToImageLibrary={toggleAddToImageLibrary}
        isEnabledLinkedElement={isEnabledLinkedElement}
        requestUpdateAndApplyChanges={requestUpdateAndApplyChanges}
      />
    );
  };

  return compose(
    connect(mapStateToProps, mapDispatchToProps),
    DragSource,
  )(FunctionElementWrapper);
};

export default ElementBase;
